/**
 * Contenedor principal del test con layout de dos paneles
 * 
 * Panel izquierdo: instrucciones y control
 * Panel derecho: preguntas y navegación
 * 
 * @param {Object} testStateManager - Gestor de estado del test
 * @author Ana Lizcano
 */

import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { getBlockTitle, getBlockInstructions, getTotalQuestions, getQuestionsByBlock } from '../data/lazyQuestions';
import useTestTimer from '../hooks/useTestTimer';
import { 
  BaseCard, 
  FlexContainer, 
  TimerDisplay, 
  InstructionBox, 
  ActionButton,
  ScrollableArea
} from '../styles/commonStyles';

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import Grid from "@mui/material/Grid";
import { styled } from "@mui/material/styles";

import BlockA from './QuestionBlocks/BlockA';
import BlockB from './QuestionBlocks/BlockB';
import BlockC from './QuestionBlocks/BlockC';
import BlockD from './QuestionBlocks/BlockD';

const TestLayout = styled(MDBox)(({ theme }) => ({
  minHeight: '80vh',
  width: '100%',
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(2),
  borderRadius: '16px',
  margin: 'auto',
}));

const TestCard = styled(BaseCard)(() => ({
  width: '100%',
  minHeight: '75vh',
  maxWidth: '1400px',
  borderRadius: '20px',
  overflow: 'hidden',
  display: 'flex',
}));

const LeftPanel = styled(MDBox)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  padding: theme.spacing(2.5),
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  width: '100%',
  position: 'relative',
  overflow: 'hidden',
}));

const RightPanel = styled(MDBox)(({ theme }) => ({
  padding: theme.spacing(2.5),
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  backgroundColor: '#ffffff',
  width: '100%',
  overflow: 'hidden',
}));

const GreetingSection = styled(MDBox)(({ theme }) => ({
  marginBottom: theme.spacing(1.5),
  flexShrink: 0,
}));

const InstructionsContainer = styled(MDBox)(() => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  minHeight: 0,
}));

const TestContainer = ({ testStateManager }) => {
  const {
    testState,
    getCurrentQuestion,
    getCurrentResponse,
    saveResponse,
    goToNextQuestion,
    goToPreviousQuestion,
    canGoNext,
    canGoPrevious,
    clearCurrentResponse,
    isLoadingQuestion
  } = testStateManager;

  const [testStarted, setTestStarted] = useState(false);

  const currentQuestion = getCurrentQuestion();
  const currentResponse = getCurrentResponse();

  const timer = useTestTimer(false, () => {});

  const handleStartTest = () => {
    setTestStarted(true);
    timer.startTimer();
  };

  const handleClearResponses = () => {
    if (clearCurrentResponse) {
      clearCurrentResponse();
    }
  };

  const totalQuestions = useMemo(() => getTotalQuestions(), []);

  const currentQuestionNumber = useMemo(() => {
    const blockCounts = getQuestionsByBlock();
    let questionNumber = 1;
    
    if (testState.currentBlock === 'A') {
      questionNumber = testState.currentQuestion + 1;
    } else if (testState.currentBlock === 'B') {
      questionNumber = blockCounts.A + testState.currentQuestion + 1;
    } else if (testState.currentBlock === 'C') {
      questionNumber = blockCounts.A + blockCounts.B + testState.currentQuestion + 1;
    } else if (testState.currentBlock === 'D') {
      questionNumber = blockCounts.A + blockCounts.B + blockCounts.C + testState.currentQuestion + 1;
    }
    
    return questionNumber;
  }, [testState.currentBlock, testState.currentQuestion]);

  const renderCurrentBlock = () => {
    const blockProps = {
      question: currentQuestion,
      response: currentResponse,
      onResponseChange: (response) => {
        saveResponse(testState.currentBlock, testState.currentQuestion, response);
      }
    };

    switch (testState.currentBlock) {
      case 'A':
        return <BlockA {...blockProps} />;
      case 'B':
        return <BlockB {...blockProps} />;
      case 'C':
        return <BlockC {...blockProps} />;
      case 'D':
        return <BlockD {...blockProps} />;
      default:
        return null;
    }
  };

  // Layout especial para Bloque D
  if (testState.currentBlock === 'D') {
    return (
      <TestLayout>
        <TestCard sx={{ flexDirection: 'column' }}>
          {/* Header con título e instrucciones */}
          <MDBox sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            padding: 3,
            textAlign: 'center',
            flexShrink: 0
          }}>
            <MDTypography variant="h4" fontWeight="bold" color="white" mb={2} sx={{ fontSize: '1.8rem' }}>
              {getBlockTitle(testState.currentBlock)}
            </MDTypography>
            
            <InstructionBox variant="gradient" sx={{ marginBottom: 2, maxWidth: '800px', margin: '0 auto' }}>
              <MDTypography variant="body1" color="white" fontWeight="medium" sx={{ whiteSpace: 'pre-line', textAlign: 'left' }}>
                {getBlockInstructions(testState.currentBlock)}
              </MDTypography>
            </InstructionBox>

            <FlexContainer justify="space-between" align="center" sx={{ maxWidth: '1200px', margin: '0 auto' }}>
              <MDTypography variant="h6" fontWeight="bold" color="white">
                Pregunta {currentQuestionNumber}/{totalQuestions}
              </MDTypography>
              
              <TimerDisplay timercolor={timer.timerColor}>
                <MDTypography variant="body1" fontWeight="bold" sx={{ color: timer.timerColor }}>
                  {timer.formattedTime}
                </MDTypography>
                <MDBox component="span" fontSize="16px" sx={{ color: timer.timerColor }}>
                  {timer.isCriticalTime ? '🔴' : timer.isWarningTime ? '🟡' : '⏱'}
                </MDBox>
              </TimerDisplay>
            </FlexContainer>
          </MDBox>

          {/* Contenido principal centrado */}
          <MDBox sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#ffffff',
            padding: 3
          }}>
            <MDBox sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {!testStarted ? (
                <FlexContainer justify="center" align="center" sx={{ minHeight: '400px' }}>
                  <MDBox textAlign="center">
                    <MDTypography variant="body1" color="dark" textAlign="center" mb={3}>
                      Presiona &quot;Empezar&quot; para comenzar el test
                    </MDTypography>
                    <MDButton
                      variant="gradient"
                      color="info"
                      size="large"
                      onClick={handleStartTest}
                      disabled={testStarted}
                      sx={{
                        borderRadius: '10px',
                        padding: '12px 32px',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        textTransform: 'none'
                      }}
                    >
                      {testStarted ? 'Test en curso...' : 'Empezar'}
                    </MDButton>
                  </MDBox>
                </FlexContainer>
              ) : isLoadingQuestion ? (
                <FlexContainer justify="center" align="center" sx={{ minHeight: '300px' }}>
                  <MDTypography variant="body1" color="dark">
                    Cargando pregunta...
                  </MDTypography>
                </FlexContainer>
              ) : currentQuestion ? (
                <MDBox sx={{ width: '100%', maxWidth: '1200px' }}>
                  {renderCurrentBlock()}
                </MDBox>
              ) : (
                <FlexContainer justify="center" align="center" sx={{ minHeight: '300px' }}>
                  <MDTypography variant="body1" color="error">
                    Error cargando la pregunta
                  </MDTypography>
                </FlexContainer>
              )}
            </MDBox>

            {/* Navegación */}
            <FlexContainer justify="space-between" align="center" sx={{
              marginTop: 2,
              paddingTop: 2,
              borderTop: '1px solid #e0e0e0',
              flexShrink: 0
            }}>
              <ActionButton
                variant="outlined"
                size="medium"
                onClick={() => goToPreviousQuestion(saveResponse)}
                disabled={!testStarted || !canGoPrevious()}
              >
                Volver
              </ActionButton>

              <ActionButton
                variant="outlined"
                size="medium"
                onClick={handleClearResponses}
                disabled={!testStarted}
              >
                Limpiar
              </ActionButton>

              <ActionButton
                variant="contained"
                size="medium"
                onClick={goToNextQuestion}
                disabled={!testStarted || !canGoNext()}
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
                  },
                  '&:disabled': {
                    background: '#e0e0e0',
                    color: '#9e9e9e',
                  }
                }}
              >
                {testState.currentBlock === 'D' && testState.currentQuestion === 0 
                  ? 'Finalizar' 
                  : 'Continuar'
                }
              </ActionButton>
            </FlexContainer>
          </MDBox>
        </TestCard>
      </TestLayout>
    );
  }

  // Layout normal para otros bloques
  return (
    <TestLayout>
      <TestCard>
        <Grid container sx={{ height: '100%' }}>
          <Grid item xs={12} md={5} sx={{ display: 'flex' }}>
            <LeftPanel>
              <GreetingSection>
                <MDTypography variant="h6" fontWeight="bold" color="white" mb={0.5}>
                  ¡Hola! 👋
                </MDTypography>
              </GreetingSection>

              <InstructionsContainer>
                <MDTypography variant="h4" fontWeight="bold" color="white" mb={2.5} textAlign="center" sx={{ fontSize: '1.8rem' }}>
                  {getBlockTitle(testState.currentBlock)}
                </MDTypography>
                
                <InstructionBox variant="gradient" sx={{ marginBottom: 1.5 }}>
                  <MDTypography variant="body2" color="white" fontWeight="medium" textAlign="center" sx={{ whiteSpace: 'pre-line' }}>
                    {getBlockInstructions(testState.currentBlock)}
                  </MDTypography>
                </InstructionBox>

                <MDBox textAlign="center">
                  <MDButton
                    variant="gradient"
                    color="light"
                    size="large"
                    onClick={handleStartTest}
                    disabled={testStarted}
                    sx={{
                      borderRadius: '10px',
                      padding: '8px 24px',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      textTransform: 'none',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 6px 16px rgba(0,0,0,0.3)',
                      },
                      '&:disabled': {
                        opacity: 0.6,
                        cursor: 'not-allowed'
                      }
                    }}
                  >
                    {testStarted ? 'Test en curso...' : 'Empezar'}
                  </MDButton>
                </MDBox>
              </InstructionsContainer>
            </LeftPanel>
          </Grid>

          <Grid item xs={12} md={7} sx={{ display: 'flex' }}>
            <RightPanel>
              <FlexContainer justify="space-between" align="center" sx={{ 
                marginBottom: 1, 
                paddingBottom: 0.1, 
                flexShrink: 0 
              }}>
                <MDTypography variant="h6" fontWeight="bold" color="dark">
                  Pregunta {currentQuestionNumber}/{totalQuestions}
                </MDTypography>
                
                <TimerDisplay timercolor={timer.timerColor}>
                  <MDTypography variant="body1" fontWeight="bold" sx={{ color: timer.timerColor }}>
                    {timer.formattedTime}
                  </MDTypography>
                  <MDBox component="span" fontSize="16px" sx={{ color: timer.timerColor }}>
                    {timer.isCriticalTime ? '🔴' : timer.isWarningTime ? '🟡' : '⏱'}
                  </MDBox>
                </TimerDisplay>
              </FlexContainer>

        

              <ScrollableArea sx={{ 
                backgroundColor: '#ffffff',
                borderRadius: '12px',
                padding: 1.5,
                border: '1px solid #e0e0e0'
              }}>
                {!testStarted ? (
                  <FlexContainer justify="center" align="center" sx={{ minHeight: '400px' }}>
                    <MDTypography variant="body1" color="dark" textAlign="center">
                      Presiona &quot;Empezar&quot; para comenzar el test
                    </MDTypography>
                  </FlexContainer>
                ) : isLoadingQuestion ? (
                  <FlexContainer justify="center" align="center" sx={{ minHeight: '300px' }}>
                    <MDTypography variant="body1" color="dark">
                      Cargando pregunta...
                    </MDTypography>
                  </FlexContainer>
                ) : currentQuestion ? (
                  renderCurrentBlock()
                ) : (
                  <FlexContainer justify="center" align="center" sx={{ minHeight: '300px' }}>
                    <MDTypography variant="body1" color="error">
                      Error cargando la pregunta
                    </MDTypography>
                  </FlexContainer>
                )}
              </ScrollableArea>

              <FlexContainer justify="space-between" align="center" sx={{
                marginTop: 1.5,
                paddingTop: 1,
                backgroundColor: '#ffffff',
                flexShrink: 0
              }}>
                <ActionButton
                  variant="outlined"
                  size="small"
                  onClick={() => goToPreviousQuestion(saveResponse)}
                  disabled={!testStarted || !canGoPrevious()}
                >
                  Volver
                </ActionButton>

                <FlexContainer gap={1}>
                  <ActionButton
                    variant="outlined"
                    size="medium"
                    onClick={handleClearResponses}
                    disabled={!testStarted}
                  >
                    Limpiar
                  </ActionButton>
                </FlexContainer>

                <ActionButton
                  variant="contained"
                  size="small"
                  onClick={goToNextQuestion}
                  disabled={!testStarted || !canGoNext()}
                  sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
                    },
                    '&:disabled': {
                      background: '#e0e0e0',
                      color: '#9e9e9e',
                    }
                  }}
                >
                  {testState.currentBlock === 'D' && testState.currentQuestion === 9 
                    ? 'Finalizar' 
                    : 'Continuar'
                  }
                </ActionButton>
              </FlexContainer>
            </RightPanel>
          </Grid>
        </Grid>
      </TestCard>
    </TestLayout>
  );
};

TestContainer.propTypes = {
  testStateManager: PropTypes.shape({
    testState: PropTypes.shape({
      currentBlock: PropTypes.string.isRequired,
      currentQuestion: PropTypes.number.isRequired
    }).isRequired,
    getCurrentQuestion: PropTypes.func.isRequired,
    getCurrentResponse: PropTypes.func.isRequired,
    saveResponse: PropTypes.func.isRequired,
    goToNextQuestion: PropTypes.func.isRequired,
    goToPreviousQuestion: PropTypes.func.isRequired,
    canGoNext: PropTypes.func.isRequired,
    canGoPrevious: PropTypes.func.isRequired,
    clearCurrentResponse: PropTypes.func.isRequired,
    isLoadingQuestion: PropTypes.bool
  }).isRequired
};

export default TestContainer;